import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Clock, X, Loader2, LogOut } from "lucide-react";
import { API } from "../../shared/config";
import { pointer, prochaineAction, horsZone, MSG_POINTAGE } from "../../lib/pointage";

const ROLES = ['Secrétaire', 'Assistant', 'Assistant(e)', 'Orthoptiste', 'Optométriste'];
const LIB = { arrivee: 'Je suis arrivé(e)', debut_pause: 'Je pars en pause', fin_pause: 'Je reprends (fin de pause)', depart: "J'ai fini ma journée" };
const jour = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Paris' });
const cleVu = (u) => `pointage_rappel_${u?.id}_${jour()}`;

const Carte = ({ children, onClose, testid }) => (
  <div className="fixed bottom-4 left-4 z-[90] w-[300px] rounded-2xl border border-gray-200 bg-white p-3 shadow-xl animate-in fade-in slide-in-from-bottom-3" data-testid={testid}>
    {onClose && <button type="button" onClick={onClose} className="absolute right-2 top-2 text-gray-400 hover:text-gray-700" aria-label="Fermer" data-testid={`${testid}-fermer`}><X className="h-4 w-4" /></button>}
    {children}
  </div>
);

const Bouton = ({ onClick, children, testid, variant = 'primary', disabled }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
    className={`w-full rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors disabled:opacity-50 ${variant === 'primary' ? 'bg-[#1173D4] text-white hover:bg-[#0e62b5]' : variant === 'warn' ? 'bg-amber-500 text-white hover:bg-amber-600' : 'border border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
    {children}
  </button>
);

// Rappel discret pour secrétaires et assistants : à la connexion (étape suivante) et à la déconnexion (pause / fin de journée).
export const PointageRappel = ({ user }) => {
  const actif = ROLES.includes(user?.role);
  const [pointage, setPointage] = useState(null);
  const [carte, setCarte] = useState(null); // { mode: 'connexion' | 'deconnexion', logout? }
  const [busy, setBusy] = useState(false);
  const [loc, setLoc] = useState(false);
  const [hz, setHz] = useState(null); // { action, reason }

  const charger = useCallback(() => axios.get(`${API}/pointage/mon-pointage/${jour()}`).then(r => { setPointage(r.data || {}); return r.data || {}; }).catch(() => null), []);

  useEffect(() => {
    if (!actif) return;
    let vu = false;
    try { vu = sessionStorage.getItem(cleVu(user)) === '1'; } catch {}
    charger().then(p => { if (p && !vu && ['arrivee', 'fin_pause'].includes(prochaineAction(p))) setCarte({ mode: 'connexion' }); });
  }, [actif, user, charger]);

  useEffect(() => {
    if (!actif) return undefined;
    window.__ophtaAvantDeconnexion = async (logout) => {
      const p = (await charger()) || {};
      if (p.arrivee && !p.depart) setCarte({ mode: 'deconnexion', logout });
      else logout();
    };
    return () => { delete window.__ophtaAvantDeconnexion; };
  }, [actif, charger]);

  const fermer = () => { try { sessionStorage.setItem(cleVu(user), '1'); } catch {} setCarte(null); setHz(null); };

  // Pointage fait ailleurs (ex. carte de « Mon espace ») → la carte de rappel se met à jour / se ferme
  useEffect(() => {
    if (!actif) return undefined;
    const sync = () => charger().then(p => { if (p && carte?.mode === 'connexion' && !['arrivee', 'fin_pause'].includes(prochaineAction(p))) setCarte(null); });
    window.addEventListener('espace-refresh', sync);
    return () => window.removeEventListener('espace-refresh', sync);
  }, [actif, carte, charger]);

  const faire = async (action, force = false) => {
    setBusy(true);
    try {
      const p = await pointer(action, { force, motif: force ? 'Pointage depuis le rappel' : undefined, onLocalisation: setLoc });
      setPointage(p);
      toast.success(MSG_POINTAGE[action]);
      const logout = carte?.logout;
      fermer();
      if (logout) setTimeout(logout, 600);
    } catch (err) {
      const d = horsZone(err);
      if (d) setHz({ action, reason: d.reason || 'hors zone' });
      else toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Erreur de pointage');
    } finally { setBusy(false); }
  };

  if (!actif || !carte || !pointage) return null;
  const titre = <p className="mb-2 flex items-center gap-1.5 pr-5 text-sm font-semibold text-gray-800"><Clock className="h-4 w-4 text-[#1173D4]" />{carte.mode === 'connexion' ? 'Pointage' : 'Avant de partir…'}</p>;

  if (hz) return (
    <Carte onClose={fermer} testid="pointage-rappel">
      {titre}
      <p className="mb-2 text-xs text-amber-700" data-testid="pointage-rappel-hors-zone">Hors zone : {hz.reason}.</p>
      <div className="space-y-1.5">
        <Bouton variant="warn" onClick={() => faire(hz.action, true)} disabled={busy} testid="pointage-rappel-forcer">Pointer quand même</Bouton>
        <Bouton variant="ghost" onClick={() => setHz(null)} testid="pointage-rappel-retour">Retour</Bouton>
      </div>
    </Carte>
  );

  const next = prochaineAction(pointage);
  const actions = carte.mode === 'connexion' ? [next] : [!pointage.debut_pause && 'debut_pause', 'depart'].filter(Boolean);
  return (
    <Carte onClose={fermer} testid="pointage-rappel">
      {titre}
      {loc && <p className="mb-2 flex items-center gap-1 text-xs text-gray-500"><Loader2 className="h-3 w-3 animate-spin" /> Localisation…</p>}
      <div className="space-y-1.5">
        {actions.map((a, i) => <Bouton key={a} variant={i === 0 ? 'primary' : 'ghost'} onClick={() => faire(a)} disabled={busy} testid={`pointage-rappel-${a}`}>{LIB[a]}</Bouton>)}
        {carte.mode === 'deconnexion' && (
          <Bouton variant="ghost" onClick={() => { const l = carte.logout; fermer(); l(); }} disabled={busy} testid="pointage-rappel-sans-pointer">
            <span className="inline-flex items-center gap-1.5"><LogOut className="h-3.5 w-3.5" /> Me déconnecter sans pointer</span>
          </Bouton>
        )}
      </div>
    </Carte>
  );
};
